<script lang="ts">
	// The design system's Card (design/components/surfaces/Card.jsx): the one
	// panel. A title turns on the tinted header strip. tone="invert" is the
	// emphasis surface, the one card that is not like the others.
	//
	// style is passed through, as the design's components all allow, so a
	// layout can make the card a column that fills its space. Without pad the
	// children sit directly in the card, which is what lets them do that.

	import type { Snippet } from 'svelte';

	interface Props {
		title?: string;
		note?: string;
		accent?: 'speed' | 'ledger';
		tone?: 'paper' | 'invert';
		pad?: boolean;
		style?: string;
		testId?: string;
		children: Snippet;
	}

	let {
		title,
		note,
		accent,
		tone = 'paper',
		pad = false,
		style,
		testId,
		children,
	}: Props = $props();
</script>

<div
	class="card {tone}"
	class:speed={accent === 'speed'}
	class:ledger={accent === 'ledger'}
	data-ground={tone === 'invert' ? 'invert' : undefined}
	data-testid={testId}
	{style}
>
	{#if title}
		<div class="head">
			<p class="title">{title}</p>
			{#if note}
				<p class="note">{note}</p>
			{/if}
		</div>
	{/if}
	{#if pad}
		<div class="pad">{@render children()}</div>
	{:else}
		{@render children()}
	{/if}
</div>

<style>
	.card {
		overflow: hidden;
	}

	.paper {
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
	}

	.invert {
		background: var(--surface-invert);
		color: var(--on-ink);
		border: none;
		border-radius: var(--radius-panel-lg);
	}

	.speed {
		border-block-start: var(--card-topline) solid var(--speed);
	}

	.ledger {
		border-block-start: var(--card-topline) solid var(--ledger);
	}

	.head {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		align-items: baseline;
		justify-content: space-between;
		padding: var(--panel-header-pad-lg);
		background: var(--surface-card-header);
		border-block-end: 1px solid var(--border-default);
	}

	/* The title takes the row and the note keeps its one line; without this
	   both shrank to fit each other and wrapped in a wide card. */
	.title {
		margin: 0;
		flex: 1 1 auto;
		min-inline-size: min(100%, 12em);
		font-size: var(--text-base);
		font-weight: var(--weight-semibold);
	}

	.note {
		margin: 0;
		flex: none;
		white-space: nowrap;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.pad {
		padding: var(--card-pad);
	}
</style>
